import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const SERVICES = [
  { name: 'Individual Counselling', desc: 'One-on-one sessions for anxiety, stress, and life transitions.' },
  { name: 'Family Therapy', desc: 'Rebuilding communication and trust within the family unit.' },
  { name: 'Marriage Counselling', desc: 'Guided support for couples navigating conflict and change.' },
  { name: 'Child & Adolescent Counselling', desc: 'Age-appropriate care for young people and teens.' },
  { name: 'Trauma Counselling', desc: 'Specialized, trauma-informed therapeutic support.' },
  { name: 'Addiction Recovery', desc: 'Structured support through recovery and relapse prevention.' },
  { name: 'Depression & Anxiety Support', desc: 'Evidence-based care for mood and anxiety concerns.' },
  { name: 'Employee Assistance (EAP)', desc: 'Confidential wellness support for corporate teams.' },
  { name: 'Psychological Assessment', desc: 'Structured assessments to guide a personalized care plan.' },
];

const STEPS = [
  { n: '01', title: 'Book', desc: 'Choose a service and a time that works across your timezone.' },
  { n: '02', title: 'AI Intake', desc: 'A guided AI assistant collects your history before your session — never a diagnosis, just preparation.' },
  { n: '03', title: 'Clinician Review', desc: 'A licensed professional reviews everything the AI gathered before you ever speak.' },
  { n: '04', title: 'Your Session', desc: 'Meet by video, audio, or chat with a human clinician who leads your care.' },
  { n: '05', title: 'Care Plan', desc: 'Receive a clinician-approved summary, and a prescription only where legally issued.' },
];

const FAQS = [
  {
    q: 'Is the AI going to diagnose me or prescribe medication?',
    a: 'No. Our AI assistant only gathers information before your appointment and helps with scheduling and reminders. Every diagnosis, treatment recommendation, and prescription comes only from a licensed clinician.',
  },
  {
    q: 'Can I book from outside Kenya?',
    a: 'Yes. Counselling is available globally. Medication prescriptions are only issued where our clinicians are legally licensed to prescribe in your location.',
  },
  {
    q: 'Is my information confidential?',
    a: 'Yes. Your records are encrypted and only accessible to your care team, in line with applicable data protection regulations.',
  },
  {
    q: 'What if I have a mental health emergency?',
    a: 'This platform is not a 24/7 emergency service. If you are in immediate danger, please contact your local emergency services right away.',
  },
];

export default function LandingPage() {
  return (
    <>
      <Hero />
      <TrustBar />
      <Services />
      <HowItWorks />
      <Team />
      <Testimonials />
      <Faqs />
      <ReferralCta />
    </>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="container hero-inner">
        <div>
          <h1 className="hero-title">Compassionate Care, Anytime, Anywhere</h1>
          <p className="hero-sub">Professional mental health support through secure video calls and AI-assisted intake — from the comfort of your home.</p>
          <div className="hero-actions">
            <a href="/register" className="btn btn-primary">Get Started</a>
            <a href="/login" className="btn btn-outline">Sign In</a>
          </div>
        </div>
        <div className="hero-graphic">
          <div className="flow-card">
            <span className="flow-label">AI Intake</span>
            <p>Guided questions before your session</p>
          </div>
          <span className="flow-arrow">→</span>
          <div className="flow-card flow-card-human">
            <span className="flow-label">Clinician</span>
            <p>Licensed professional leads your care</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ['Licensed Clinicians', 'End-to-End Encrypted', 'HIPAA-Aligned', 'Available 24/7'];
  return (
    <section className="trust-bar">
      <div className="container trust-bar-inner">
        {items.map((item) => (
          <span key={item} className="trust-item">{item}</span>
        ))}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="section services-section">
      <div className="container">
        <span className="eyebrow">What We Offer</span>
        <h2>Our Services</h2>
        <div className="services-grid">
          {SERVICES.map((s) => (
            <div className="service-card" key={s.name}>
              <h3>{s.name}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section how-section">
      <div className="container">
        <span className="eyebrow">Simple Process</span>
        <h2>How It Works</h2>
        <div className="steps-grid">
          {STEPS.map((s) => (
            <div className="step-card" key={s.n}>
              <span className="step-number">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section className="section team-section">
      <div className="container">
        <span className="eyebrow">Our People</span>
        <h2>Meet the Team</h2>
        <p className="team-intro">Our clinicians are licensed, experienced, and passionate about making mental health care accessible.</p>
      </div>
    </section>
  );
}

function Testimonials() {
  const testimonials = [
    { name: 'Sarah M.', text: 'The AI intake was surprisingly helpful — by the time I spoke to my counsellor, they already understood what I was going through.' },
    { name: 'James K.', text: 'Booking was easy and the video session quality was excellent. I felt heard and supported throughout.' },
    { name: 'Amina O.', text: 'As a referring doctor, the process was seamless. My patient got follow-up within 24 hours.' },
  ];
  return (
    <section className="section testimonials-section">
      <div className="container">
        <span className="eyebrow">What People Say</span>
        <h2>Testimonials</h2>
        <div className="testimonials-grid">
          {testimonials.map((t) => (
            <div className="testimonial-card" key={t.name}>
              <p className="testimonial-text">"{t.text}"</p>
              <span className="testimonial-name">— {t.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faqs() {
  return (
    <section className="section">
      <div className="container">
        <span className="eyebrow">Good to Know</span>
        <h2>Frequently asked questions</h2>
        <div className="faq-list">
          {FAQS.map((f) => (
            <details className="faq-item" key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReferralCta() {
  return (
    <section className="referral-cta">
      <div className="container referral-cta-inner">
        <div>
          <h2 style={{ color: 'var(--color-white)' }}>Referring a patient or client?</h2>
          <p>
            Hospitals, NGOs, schools, and employers can submit a referral in minutes and
            track its status.
          </p>
        </div>
        <Link to="/refer" className="btn btn-primary">Submit a Referral</Link>
      </div>
    </section>
  );
}
